import { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router"
import {
    Box, Button, ButtonGroup, Flex, Grid, Input,
    NumberInput, NumberInputField, Select, Spinner, Text, useToast
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Breadcrumb, Container, Form, Heading, Icon, IconButton } from "components"
import { API, Mask } from "config"
import { useAllDistributors, useSuppliers } from "hooks"

// ─── SectionCard ──────────────────────────────────────────────────────────────

const SectionCard = ({ title, subtitle, icon, color = 'teal', children, extra }) => (
    <Flex direction="column" h="100%"
        borderRadius="2xl" overflow="hidden"
        border="1px solid" borderColor="gray.200"
        _dark={{ borderColor: 'gray.700' }}
        boxShadow="0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -1px rgba(0,0,0,0.04)"
    >
        <Flex align="center" gap={3} px={6} py={4}
            bg="gray.800" _dark={{ bg: 'gray.900' }}
        >
            <Flex align="center" justify="center"
                w={10} h={10} borderRadius="xl" flexShrink={0}
                bgGradient={`linear(to-br, ${color}.400, ${color}.600)`}
                boxShadow="0 2px 8px rgba(0,0,0,0.25)"
            >
                <Icon name={icon} boxSize={4} color="white" />
            </Flex>
            <Box flex={1} minW={0}>
                <Text fontSize="sm" fontWeight="800" color="white" letterSpacing="0.025em">
                    {title}
                </Text>
                {subtitle && (
                    <Text fontSize="xs" color="gray.400" mt={0.5} noOfLines={1}>{subtitle}</Text>
                )}
            </Box>
            {extra && <Box flexShrink={0}>{extra}</Box>}
        </Flex>
        <Box px={6} py={6} bg="white" _dark={{ bg: 'gray.800' }} flex={1}>
            {children}
        </Box>
    </Flex>
)

// ─── FieldLabel ───────────────────────────────────────────────────────────────

const FieldLabel = ({ children, required, hint }) => (
    <Box mb={1.5}>
        <Text fontSize="sm" fontWeight="600" color="gray.700" _dark={{ color: 'gray.200' }}>
            {children}{required && <Box as="span" color="red.400" ml={0.5}>*</Box>}
        </Text>
        {hint && <Text fontSize="xs" color="gray.400" _dark={{ color: 'gray.500' }} mt={0.5}>{hint}</Text>}
    </Box>
)

// ─── FileUploadZone ────────────────────────────────────────────────────────────

const FileUploadZone = ({ file, onFile, error, accept, hint, colorScheme = 'teal' }) => {
    const inputRef = useRef()
    const cs = colorScheme

    const ext = file?.name?.split('.').pop()?.toLowerCase()
    const typeLabel = ext === 'xml' ? 'XML NF-e'
        : ext === 'pdf' ? 'PDF'
        : ['jpg','jpeg','png','webp'].includes(ext) ? 'Imagem'
        : ['mp4','mov','avi'].includes(ext) ? 'Vídeo'
        : file ? 'Arquivo' : null

    if (file) {
        return (
            <Flex align="center" gap={3}
                border="1.5px solid" borderColor={`${cs}.300`} bg={`${cs}.50`}
                _dark={{ bg: `${cs}.900`, borderColor: `${cs}.500` }}
                borderRadius="lg" px={4} py={3}
            >
                <Icon name="check" boxSize={5} color={`${cs}.500`} />
                <Box flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" color={`${cs}.700`}
                        _dark={{ color: `${cs}.200` }} noOfLines={1}>
                        {file.name}
                    </Text>
                    <Text fontSize="xs" color={`${cs}.500`}>{typeLabel} · {(file.size / 1024).toFixed(0)} KB</Text>
                </Box>
                <Button size="xs" variant="ghost" colorScheme="gray"
                    onClick={() => { onFile(null); if (inputRef.current) inputRef.current.value = '' }}>
                    Trocar
                </Button>
            </Flex>
        )
    }

    return (
        <>
            <Flex
                as="button" type="button" direction="column" align="center" justify="center"
                gap={2} w="100%" minH="80px" borderRadius="lg"
                border="2px dashed" borderColor={error ? 'red.400' : 'gray.300'}
                _dark={{ borderColor: error ? 'red.500' : 'gray.600' }}
                bg={error ? 'red.50' : 'gray.50'} _dark={{ bg: 'gray.750' }}
                _hover={{ borderColor: `${cs}.400`, bg: `${cs}.50` }}
                transition="all 0.2s" cursor="pointer" px={4}
                onClick={() => inputRef.current?.click()}
            >
                <Icon name="upload" boxSize={6} color={error ? 'red.400' : 'gray.400'} />
                <Text fontSize="sm" color={error ? 'red.500' : 'gray.500'} _dark={{ color: error ? 'red.400' : 'gray.400' }}>
                    Clique para selecionar ou arraste o arquivo
                </Text>
                <Text fontSize="xs" color="gray.400">{hint}</Text>
            </Flex>
            {error && <Text fontSize="xs" color="red.500" mt={1}>{error}</Text>}
            <input ref={inputRef} type="file" accept={accept}
                style={{ display: 'none' }}
                onChange={e => onFile(e.target.files?.[0] ?? null)} />
        </>
    )
}

// ─── SupplierSearch ───────────────────────────────────────────────────────────

const SupplierSearch = ({ value, onSelect }) => {
    const [term, setTerm] = useState(value || '')
    const [open, setOpen] = useState(false)
    const suppliers       = useSuppliers()
    const allSuppliers    = suppliers.data ?? []

    useEffect(() => { setTerm(value || '') }, [value])

    const filtered = term.length >= 2
        ? allSuppliers.filter(s => s.name?.toLowerCase().includes(term.toLowerCase())).slice(0, 8)
        : []

    const handleSelect = (s) => { onSelect(s.name); setTerm(s.name); setOpen(false) }
    const handleChange = (e) => { setTerm(e.target.value); onSelect(e.target.value); setOpen(true) }

    return (
        <Box position="relative">
            <Input size="md" value={term}
                placeholder="Busque ou digite o nome do fornecedor..."
                onChange={handleChange}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 180)}
            />
            {open && filtered.length > 0 && (
                <Box position="absolute" top="calc(100% + 4px)" left={0} right={0}
                    bg="white" border="1px solid" borderColor="gray.200"
                    _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                    borderRadius="md" boxShadow="lg" zIndex={1500} maxH="200px" overflowY="auto"
                >
                    {filtered.map(s => (
                        <Box key={s.id} px={4} py={2.5} cursor="pointer"
                            _hover={{ bg: 'blue.50' }} _dark={{ _hover: { bg: 'gray.700' } }}
                            onMouseDown={() => handleSelect(s)}
                        >
                            <Text fontSize="sm" fontWeight="medium">{s.name}</Text>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

// ─── ProductSearch ────────────────────────────────────────────────────────────

const ProductSearch = ({ value, label, onSelect, xmlHint }) => {
    const [term, setTerm]               = useState(label || '')
    const [debouncedTerm, setDebounced] = useState('')
    const [open, setOpen]               = useState(false)
    const wrapRef = useRef()

    useEffect(() => {
        if (!term || term === label) return
        const t = setTimeout(() => setDebounced(term), 350)
        return () => clearTimeout(t)
    }, [term, label])

    const { data: products, isFetching } = useQuery({
        queryKey: ['product-search', debouncedTerm],
        queryFn: async () => {
            const res = await API.get(`/products?term=${debouncedTerm}`)
            return Array.isArray(res.data) ? res.data : res.data?.data ?? []
        },
        enabled: debouncedTerm.length >= 2,
        staleTime: 30000,
    })

    const handleSelect = (p) => { onSelect(p); setTerm(p.name); setOpen(false) }
    const handleClear  = () => { onSelect(null); setTerm(''); setDebounced('') }

    if (value) {
        return (
            <Flex align="center" border="1.5px solid" borderColor="teal.300"
                bg="teal.50" _dark={{ bg: 'teal.900', borderColor: 'teal.500' }}
                borderRadius="md" px={3} gap={2} h="40px"
            >
                <Icon name="product" boxSize={4} color="teal.500" />
                <Text fontSize="sm" flex={1} noOfLines={1} fontWeight="semibold"
                    color="teal.700" _dark={{ color: 'teal.200' }}>
                    {label || value}
                </Text>
                <IconButton icon="cancel" size="sm" variant="ghost" colorScheme="red"
                    aria-label="Remover produto" onClick={handleClear} />
            </Flex>
        )
    }

    return (
        <Box position="relative" ref={wrapRef}>
            {xmlHint && (
                <Text fontSize="xs" color="orange.500" _dark={{ color: 'orange.300' }} mb={1}>
                    XML: <Box as="span" fontWeight="semibold">{xmlHint}</Box> — busque e vincule o produto cadastrado
                </Text>
            )}
            <Input size="md" value={term} placeholder="Buscar produto por nome..."
                onChange={e => { setTerm(e.target.value); setOpen(true) }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 180)}
            />
            {open && term.length >= 2 && (
                <Box position="absolute" top="calc(100% + 4px)" left={0} right={0}
                    bg="white" border="1px solid" borderColor="gray.200"
                    _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                    borderRadius="md" boxShadow="lg" zIndex={1500} maxH="220px" overflowY="auto"
                >
                    {isFetching && <Flex justify="center" py={4}><Spinner size="sm" /></Flex>}
                    {!isFetching && (!products || products.length === 0) && (
                        <Text fontSize="sm" color="gray.500" _dark={{ color: 'gray.400' }} px={4} py={3}>
                            Nenhum produto encontrado
                        </Text>
                    )}
                    {(products ?? []).slice(0, 12).map(p => (
                        <Box key={p.id} px={4} py={2.5} cursor="pointer"
                            _hover={{ bg: 'teal.50' }} _dark={{ _hover: { bg: 'gray.700' } }}
                            onMouseDown={() => handleSelect(p)}
                        >
                            <Text fontSize="sm" fontWeight="semibold">{p.name}</Text>
                            {p.cod && <Text fontSize="xs" color="gray.400" _dark={{ color: 'gray.500' }}>Cód: {p.cod}</Text>}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

// ─── InvoiceItemForm ──────────────────────────────────────────────────────────

const EMPTY_LOT  = { lot_number: '', quantity: 1, expires_at: '' }
const EMPTY_ITEM = { product_id: '', product_name: '', xml_product_name: '', ordered_qty: 1, unit_cost: '', lots: [{ ...EMPTY_LOT }] }

const InvoiceItemForm = ({ item, index, onChange, onRemove, canRemove, itemError }) => {
    const addLot    = () => onChange({ ...item, lots: [...item.lots, { ...EMPTY_LOT }] })
    const removeLot = (j) => {
        if (item.lots.length <= 1) return
        onChange({ ...item, lots: item.lots.filter((_, i) => i !== j) })
    }
    const setLot = (j, key, val) =>
        onChange({ ...item, lots: item.lots.map((l, i) => i === j ? { ...l, [key]: val } : l) })

    const hasError    = !!itemError
    const headerColor = hasError ? 'red' : item.product_id ? 'teal' : 'gray'

    return (
        <Box
            border="1.5px solid"
            borderColor={hasError ? 'red.400' : item.product_id ? 'teal.400' : 'gray.200'}
            _dark={{ borderColor: hasError ? 'red.500' : item.product_id ? 'teal.600' : 'gray.600' }}
            borderRadius="xl" overflow="hidden" transition="border-color 0.2s"
        >
            <Flex align="center" gap={3} px={5} py={3}
                bgGradient={
                    hasError
                        ? 'linear(to-r, red.700, red.800)'
                        : item.product_id
                            ? 'linear(to-r, teal.700, teal.800)'
                            : 'linear(to-r, gray.600, gray.700)'
                }
                _dark={{
                    bgGradient: hasError
                        ? 'linear(to-r, red.900, red.800)'
                        : item.product_id
                            ? 'linear(to-r, teal.900, teal.800)'
                            : 'linear(to-r, gray.800, gray.750)'
                }}
            >
                <Flex align="center" justify="center"
                    w={7} h={7} borderRadius="full" flexShrink={0}
                    bg="white" fontSize="xs" fontWeight="900"
                    color={`${headerColor}.600`}
                >
                    {index + 1}
                </Flex>
                <Text fontSize="sm" fontWeight="700" color="white" flex={1} noOfLines={1}>
                    {item.product_name || item.xml_product_name || `Item ${index + 1}`}
                </Text>
                {canRemove && (
                    <Box as="button" onClick={onRemove} lineHeight={0} p={1.5} borderRadius="md"
                        color="whiteAlpha.600" _hover={{ color: 'red.300', bg: 'blackAlpha.300' }}
                        transition="all 0.15s"
                    >
                        <Icon name="cancel" boxSize={4} />
                    </Box>
                )}
            </Flex>

            <Box p={4} bg="white" _dark={{ bg: 'gray.800' }}>
                <Grid templateColumns={{ base: '1fr', md: '1fr 100px 130px' }} gap={4} mb={4}>
                    <Box>
                        <FieldLabel required>Produto</FieldLabel>
                        <ProductSearch
                            value={item.product_id} label={item.product_name}
                            xmlHint={!item.product_id ? item.xml_product_name : null}
                            onSelect={p => onChange({ ...item, product_id: p?.id ?? '', product_name: p?.name ?? '' })}
                        />
                    </Box>
                    <Box>
                        <FieldLabel required>Qtd.</FieldLabel>
                        <NumberInput size="md" min={1} value={item.ordered_qty}
                            onChange={v => onChange({ ...item, ordered_qty: v })}>
                            <NumberInputField placeholder="1" />
                        </NumberInput>
                    </Box>
                    <Box>
                        <FieldLabel>Custo un. (R$)</FieldLabel>
                        <Input size="md" placeholder="0,00"
                            value={item.unit_cost}
                            onChange={e => onChange({ ...item, unit_cost: e.target.value ? Mask.price(e.target.value) : '' })} />
                    </Box>
                </Grid>

                {/* Lotes */}
                <Box
                    bg="purple.50" border="1px dashed" borderColor="purple.200"
                    _dark={{ bg: 'rgba(128,90,213,0.12)', borderColor: 'purple.600' }}
                    borderRadius="lg" p={4}
                >
                    <Flex align="center" justify="space-between" mb={3}>
                        <Flex align="center" gap={2}>
                            <Box w="2px" h="14px" bg="purple.400" borderRadius="full" />
                            <Text fontSize="xs" fontWeight="700" color="purple.700"
                                _dark={{ color: 'purple.300' }} letterSpacing="wider" textTransform="uppercase">
                                Lotes / Validade <Box as="span" color="red.400">*</Box>
                            </Text>
                        </Flex>
                        <Button size="sm" variant="ghost" colorScheme="purple" onClick={addLot}
                            leftIcon={<Icon name="new" boxSize={3.5} />}>
                            Adicionar Lote
                        </Button>
                    </Flex>

                    {itemError && (
                        <Text fontSize="xs" color="red.500" mb={2}>{itemError}</Text>
                    )}

                    <Flex direction="column" gap={2}>
                        {item.lots.map((lot, j) => (
                            <Grid key={j} templateColumns="1fr 90px 155px auto" gap={2} alignItems="center">
                                <Box>
                                    {j === 0 && <Text fontSize="xs" color="purple.600" _dark={{ color: 'purple.400' }} mb={1}>Nº do Lote</Text>}
                                    <Input size="sm" placeholder="Ex: 25.0312" value={lot.lot_number}
                                        onChange={e => setLot(j, 'lot_number', e.target.value)}
                                        isInvalid={!lot.lot_number.trim()}
                                        bg="white" _dark={{ bg: 'gray.700' }} />
                                </Box>
                                <Box>
                                    {j === 0 && <Text fontSize="xs" color="purple.600" _dark={{ color: 'purple.400' }} mb={1}>Qtd.</Text>}
                                    <NumberInput size="sm" min={1} value={lot.quantity}
                                        onChange={v => setLot(j, 'quantity', v)}>
                                        <NumberInputField placeholder="1" bg="white" _dark={{ bg: 'gray.700' }} />
                                    </NumberInput>
                                </Box>
                                <Box>
                                    {j === 0 && <Text fontSize="xs" color="purple.600" _dark={{ color: 'purple.400' }} mb={1}>Validade</Text>}
                                    <Input size="sm" type="date" title="Data de validade" value={lot.expires_at}
                                        onChange={e => setLot(j, 'expires_at', e.target.value)}
                                        bg="white" _dark={{ bg: 'gray.700' }} />
                                </Box>
                                <Box mt={j === 0 ? 5 : 0}>
                                    <IconButton icon="cancel" size="sm" variant="ghost" colorScheme="red"
                                        onClick={() => removeLot(j)} aria-label="Remover lote"
                                        isDisabled={item.lots.length <= 1} />
                                </Box>
                            </Grid>
                        ))}
                    </Flex>
                </Box>
            </Box>
        </Box>
    )
}

// ─── Página ───────────────────────────────────────────────────────────────────

export default function NewInvoice() {
    const navigate      = useNavigate()
    const location      = useLocation()
    const toast         = useToast()
    const queryClient   = useQueryClient()
    const distributors  = useAllDistributors()

    // Dados vindos de importação XML na página anterior
    const routeState = location.state ?? {}

    const [loading, setLoading] = useState(false)
    const [errors, setErrors]   = useState({})
    const [form, setForm] = useState({
        number:      routeState.parsed?.number      ?? '',
        supplier_name: routeState.parsed?.supplier_name ?? '',
        distributor_id: '',
        key:         routeState.parsed?.key         ?? '',
        issued_at:   routeState.parsed?.issued_at   ?? '',
        notes:       '',
    })

    // Items: pré-preenchidos com dados do XML/PDF (sem product_id — usuário precisa vincular,
    // exceto quando o backend encontrou uma sugestão de produto)
    const [items, setItems] = useState(() => {
        const parsedItems = routeState.parsed?.items
        if (parsedItems?.length) {
            return parsedItems.map(it => ({
                ...EMPTY_ITEM,
                xml_product_name: it.product_name,
                // Pré-vincula automaticamente se o backend encontrou uma correspondência
                product_id:   it.suggested_product?.id   ?? '',
                product_name: it.suggested_product?.name ?? '',
                ordered_qty:  it.ordered_qty || 1,
                unit_cost:    it.unit_cost ? Mask.price(Math.round(parseFloat(it.unit_cost) * 100)) : '',
                lots: it.lots?.length
                    ? it.lots.map(l => ({
                        lot_number: l.lot_number || '',
                        quantity:   l.quantity   || 1,
                        expires_at: l.expires_at || '',
                    }))
                    : [{ ...EMPTY_LOT }],
            }))
        }
        return [{ ...EMPTY_ITEM, lots: [{ ...EMPTY_LOT }] }]
    })

    // Documento fiscal (XML/PDF da NF-e) — obrigatório
    const [fiscalFile, setFiscalFile]             = useState(routeState.inputFile ?? null)
    // Arquivo de conferência (foto/vídeo dos produtos) — opcional
    const [verificationFile, setVerificationFile] = useState(null)

    const set        = (key, val) => { setForm(f => ({ ...f, [key]: val })); setErrors(e => ({ ...e, [key]: null })) }
    const addItem    = () => setItems(prev => [...prev, { ...EMPTY_ITEM, lots: [{ ...EMPTY_LOT }] }])
    const removeItem = (i) => setItems(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev)
    const setItem    = (i, val) => setItems(prev => prev.map((it, idx) => idx === i ? val : it))

    const warehouses  = (distributors.data ?? []).filter(d => d.is_warehouse)
    const filledCount = items.filter(i => i.product_id).length
    const wasImported = !!routeState.parsed
    const importSource = routeState.source  // 'xml' | 'pdf' | undefined

    const validate = () => {
        const e = {}
        if (!form.number.trim())        e.number         = 'Informe o número da NF-e'
        if (!form.supplier_name.trim()) e.supplier_name  = 'Informe o fornecedor'
        if (!form.distributor_id)       e.distributor_id = 'Selecione o núcleo de destino'
        if (form.key && form.key.length !== 44) e.key    = 'A chave deve ter 44 dígitos'
        if (!fiscalFile)                e.fiscalFile     = 'Anexe o documento fiscal (XML ou PDF da NF-e)'

        const validItems = items.filter(it => it.product_id)
        if (!validItems.length) e.items = 'Selecione ao menos um produto'

        const itemErrors = {}
        validItems.forEach((it, i) => {
            const hasLot = it.lots.some(l => l.lot_number.trim())
            if (!hasLot) itemErrors[i] = 'Informe ao menos um lote com número preenchido'
        })
        if (Object.keys(itemErrors).length) e.itemErrors = itemErrors

        setErrors(e)
        return Object.keys(e).length === 0
    }

    const handleSubmit = async () => {
        if (!validate()) return

        const validItems = items.filter(it => it.product_id)

        // Montar FormData (backend exige multipart com o arquivo de evidência)
        const fd = new FormData()
        fd.append('file', fiscalFile)
        if (verificationFile) fd.append('verification_file', verificationFile)
        fd.append('number',      form.number)
        fd.append('supplier_name', form.supplier_name)
        fd.append('distributor_id', form.distributor_id)
        if (form.key)       fd.append('key', form.key)
        if (form.issued_at) fd.append('issued_at', form.issued_at)
        if (form.notes)     fd.append('notes', form.notes)

        validItems.forEach((item, i) => {
            fd.append(`items[${i}][product_id]`,   item.product_id)
            fd.append(`items[${i}][ordered_qty]`,  Number(item.ordered_qty))
            if (item.unit_cost) fd.append(`items[${i}][unit_cost]`, parseInt(item.unit_cost.replace(/\D/g, '')) / 100)
            item.lots
                .filter(l => l.lot_number.trim())
                .forEach((lot, j) => {
                    fd.append(`items[${i}][lots][${j}][lot_number]`, lot.lot_number)
                    fd.append(`items[${i}][lots][${j}][quantity]`,   Number(lot.quantity))
                    if (lot.expires_at) fd.append(`items[${i}][lots][${j}][expires_at]`, lot.expires_at)
                })
        })

        setLoading(true)
        try {
            const res = await API.post('/invoices', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            toast({ title: 'NF-e registrada com sucesso', status: 'success', duration: 4000 })
            queryClient.invalidateQueries({ queryKey: ['invoices-list'] })
            navigate(`/stock/invoices/${res.data.id}`)
        } catch (e) {
            const msg = e?.response?.data?.message ?? 'Erro ao registrar NF-e'
            toast({ title: msg, status: 'error', duration: 6000 })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Container maxW="90%" mt={10} pb={20}>
            <Breadcrumb items={[
                { label: 'NF-e / Entradas', to: '/stock/invoices' },
                { label: 'Nova NF-e' },
            ]} mb={4} />

            <Heading title="Registrar Nova NF-e"
                description="Preencha os dados fiscais e os lotes de cada item recebido">
                <ButtonGroup size="sm">
                    <Button variant="ghost" onClick={() => navigate('/stock/invoices')} isDisabled={loading}>
                        Cancelar
                    </Button>
                    <Button colorScheme="teal" isLoading={loading} onClick={handleSubmit}
                        leftIcon={<Icon name="check" />} loadingText="Registrando...">
                        Registrar NF-e
                    </Button>
                </ButtonGroup>
            </Heading>

            {/* Banner de importação bem-sucedida */}
            {wasImported && importSource === 'xml' && (
                <Flex align="center" gap={3} mt={4}
                    bg="purple.50" border="1px solid" borderColor="purple.200"
                    _dark={{ bg: 'purple.900', borderColor: 'purple.600' }}
                    borderRadius="lg" px={5} py={3}
                >
                    <Icon name="check" boxSize={5} color="purple.500" />
                    <Box>
                        <Text fontSize="sm" fontWeight="bold" color="purple.700" _dark={{ color: 'purple.200' }}>
                            NF-e importada via XML
                        </Text>
                        <Text fontSize="xs" color="purple.500">
                            Dados pré-preenchidos incluindo itens e lotes. Vincule cada produto ao seu cadastro e revise antes de registrar.
                        </Text>
                    </Box>
                </Flex>
            )}
            {wasImported && importSource === 'pdf' && (
                <Flex align="center" gap={3} mt={4}
                    bg="orange.50" border="1px solid" borderColor="orange.200"
                    _dark={{ bg: 'orange.900', borderColor: 'orange.600' }}
                    borderRadius="lg" px={5} py={3}
                >
                    <Icon name="view" boxSize={5} color="orange.500" />
                    <Box>
                        <Text fontSize="sm" fontWeight="bold" color="orange.700" _dark={{ color: 'orange.200' }}>
                            Importado via PDF DANFE
                        </Text>
                        <Text fontSize="xs" color="orange.500">
                            {routeState.message ?? 'Dados extraídos automaticamente. Revise e vincule cada produto antes de registrar.'}
                        </Text>
                    </Box>
                </Flex>
            )}

            <Grid
                templateColumns={{ base: '1fr', xl: '400px 1fr' }}
                gap={6} mt={6} alignItems="stretch"
            >
                {/* Coluna esquerda: dados da nota + evidência */}
                <Box>
                    <SectionCard
                        title="Dados da Nota Fiscal"
                        subtitle="Informações do documento fiscal"
                        icon="check" color="blue"
                    >
                        <Flex direction="column" gap={5}>
                            <Grid templateColumns="1fr 1fr" gap={4}>
                                <Box>
                                    <FieldLabel required>Número da NF-e</FieldLabel>
                                    <Input size="md" value={form.number} placeholder="000.064.179"
                                        onChange={e => set('number', e.target.value)}
                                        isInvalid={!!errors.number} />
                                    {errors.number && <Text fontSize="xs" color="red.500" mt={1}>{errors.number}</Text>}
                                </Box>
                                <Box>
                                    <FieldLabel>Data de Emissão</FieldLabel>
                                    <Input size="md" type="date" value={form.issued_at}
                                        onChange={e => set('issued_at', e.target.value)} />
                                </Box>
                            </Grid>

                            <Box>
                                <FieldLabel required>Fornecedor</FieldLabel>
                                <SupplierSearch value={form.supplier_name}
                                    onSelect={name => set('supplier_name', name)} />
                                {errors.supplier_name && <Text fontSize="xs" color="red.500" mt={1}>{errors.supplier_name}</Text>}
                            </Box>

                            <Box>
                                <FieldLabel required>Núcleo de Destino</FieldLabel>
                                {distributors.isLoading ? (
                                    <Flex align="center" gap={2} h="40px">
                                        <Spinner size="sm" />
                                        <Text fontSize="sm" color="gray.500" _dark={{ color: 'gray.400' }}>Carregando...</Text>
                                    </Flex>
                                ) : (
                                    <Select size="md" value={form.distributor_id}
                                        onChange={e => set('distributor_id', e.target.value)}
                                        isInvalid={!!errors.distributor_id}
                                        placeholder="Selecione o núcleo de destino">
                                        {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </Select>
                                )}
                                {errors.distributor_id && <Text fontSize="xs" color="red.500" mt={1}>{errors.distributor_id}</Text>}
                            </Box>

                            <Box>
                                <FieldLabel hint="44 dígitos numéricos">Chave de Acesso</FieldLabel>
                                <Input size="md" value={form.key} maxLength={44}
                                    placeholder="52260611878065000150550030000641791046403272"
                                    onChange={e => set('key', e.target.value.replace(/\D/g, '').slice(0, 44))}
                                    isInvalid={!!errors.key} fontFamily="mono" fontSize="sm" />
                                {errors.key && <Text fontSize="xs" color="red.500" mt={1}>{errors.key}</Text>}
                                {form.key.length > 0 && !errors.key && (
                                    <Text fontSize="xs" color="gray.400" _dark={{ color: 'gray.500' }} mt={1}>
                                        {form.key.length}/44 dígitos
                                    </Text>
                                )}
                            </Box>

                            <Box>
                                <FieldLabel>Observações</FieldLabel>
                                <Form.Textarea size="md" value={form.notes} rows={3}
                                    placeholder="Informações adicionais (opcional)"
                                    onChange={e => set('notes', e.target.value)} />
                            </Box>

                            {/* Documento fiscal — obrigatório */}
                            <Box>
                                <FieldLabel required hint="XML da NF-e (dados completos) ou PDF DANFE">
                                    Documento Fiscal
                                </FieldLabel>
                                <FileUploadZone
                                    file={fiscalFile}
                                    onFile={f => { setFiscalFile(f); setErrors(e => ({ ...e, fiscalFile: null })) }}
                                    error={errors.fiscalFile}
                                    accept=".xml,.pdf"
                                    hint="XML NF-e ou PDF DANFE — até 20 MB"
                                    colorScheme="blue"
                                />
                            </Box>

                            {/* Conferência dos produtos — opcional */}
                            <Box>
                                <FieldLabel hint="Foto ou vídeo da conferência dos produtos recebidos">
                                    Conferência de Produtos
                                    <Box as="span" fontSize="xs" fontWeight="normal" color="gray.400" ml={2}>(opcional)</Box>
                                </FieldLabel>
                                <FileUploadZone
                                    file={verificationFile}
                                    onFile={setVerificationFile}
                                    accept=".jpg,.jpeg,.png,.webp,.mp4,.mov"
                                    hint="JPG, PNG, MP4, MOV — até 20 MB"
                                    colorScheme="orange"
                                />
                            </Box>
                        </Flex>
                    </SectionCard>
                </Box>

                {/* Coluna direita: itens da NF-e */}
                <Box>
                    <SectionCard
                        title="Itens da NF-e — Lotes Obrigatórios"
                        subtitle={filledCount > 0
                            ? `${filledCount} produto(s) adicionado(s)`
                            : 'Informe o lote de cada produto recebido'}
                        icon="product" color="teal"
                    >
                        {errors.items && (
                            <Box bg="red.50" border="1px solid" borderColor="red.200"
                                _dark={{ bg: 'red.900', borderColor: 'red.700' }}
                                borderRadius="md" px={4} py={3} mb={4}>
                                <Text fontSize="sm" color="red.600" _dark={{ color: 'red.300' }}>{errors.items}</Text>
                            </Box>
                        )}

                        <Flex direction="column" gap={4}>
                            {items.map((item, i) => (
                                <InvoiceItemForm
                                    key={i} index={i} item={item}
                                    canRemove={items.length > 1}
                                    onChange={val => setItem(i, val)}
                                    onRemove={() => removeItem(i)}
                                    itemError={errors.itemErrors?.[i]}
                                />
                            ))}
                        </Flex>

                        <Flex justify="center" mt={5}>
                            <Button size="md" variant="outline" colorScheme="teal"
                                leftIcon={<Icon name="new" />} onClick={addItem}>
                                Adicionar Outro Produto
                            </Button>
                        </Flex>
                    </SectionCard>
                </Box>
            </Grid>
        </Container>
    )
}
